/**
 * A confirmation that looks like the rest of the app, instead of the browser's
 * grey system box. Same `<dialog>` as the editing popup, so both read as one
 * interface.
 */

export interface ConfirmDialog {
  el: HTMLDialogElement;
  ask(message: string): Promise<boolean>;
}

export function createConfirmDialog(
  confirmLabel = 'Abandonner',
  cancelLabel = 'Annuler',
): ConfirmDialog {
  const text = document.createElement('p');
  text.className = 'confirm-message';

  const confirm = document.createElement('button');
  confirm.type = 'button';
  confirm.className = 'danger';
  confirm.textContent = confirmLabel;

  const cancel = document.createElement('button');
  cancel.type = 'button';
  cancel.dataset['primary'] = '';
  cancel.textContent = cancelLabel;

  const actions = document.createElement('div');
  actions.className = 'form-row confirm-actions';
  actions.append(cancel, confirm);

  const el = document.createElement('dialog');
  el.className = 'confirm-dialog';
  el.dataset['panel'] = 'confirm';
  el.append(text, actions);

  let settle: ((answer: boolean) => void) | null = null;

  const answer = (value: boolean) => {
    const resolve = settle;
    settle = null;
    if (el.open) el.close();
    resolve?.(value);
  };

  confirm.addEventListener('click', () => answer(true));
  cancel.addEventListener('click', () => answer(false));
  // Escape closes the dialog on its own; treat it as a refusal.
  el.addEventListener('cancel', () => answer(false));

  // Clicking outside the sheet is the same refusal. The backdrop is part of the
  // dialog's box, so a click on it targets the dialog element itself.
  el.addEventListener('click', (event) => {
    if (event.target !== el) return;
    const box = el.getBoundingClientRect();
    const inside =
      event.clientX >= box.left &&
      event.clientX <= box.right &&
      event.clientY >= box.top &&
      event.clientY <= box.bottom;
    if (!inside) answer(false);
  });

  return {
    el,
    ask(message: string): Promise<boolean> {
      text.textContent = message;
      return new Promise((resolve) => {
        settle = resolve;
        el.showModal();
        // The safe choice is focused, so Enter never discards work.
        cancel.focus();
      });
    },
  };
}
